import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Grid,
  Modal,
  Segmented,
  Select,
  Space,
  Spin,
  Table,
  Tabs,
  Typography,
} from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { Notice } from '../components/common/Notice';
import { auditTrailService } from '../services/audit-trail.service';
import {
  PublicAuditTrailEntriesResult,
  PublicAuditTrailEntryDetail,
  PublicAuditTrailSummary,
  QuantitativeSummaryResponse,
} from '../types/audit-trail.types';
import { SurveyStatus } from '../types/survey.types';
import PublicAuditTrailHeader from '../components/survey/PublicAuditTrailHeader';
import PublicAuditTrailEntries from '../components/survey/PublicAuditTrailEntries';
import PublicAuditTrailQuantitativeSummary from '../components/survey/PublicAuditTrailQuantitativeSummary';
import { SurveyLandingCTA } from '../components/survey/SurveyLandingCTA';
import AnalysisSummaryView from '../components/analytics/AnalysisSummaryView';
import AnalyticsSegmentedView from '../components/analytics/AnalyticsSegmentedView';
import { PublicHomeLayout } from '../components/layout/PublicHomeLayout';
import { useAuth } from '../hooks/useAuth';
import { surveyLandingService } from '../services/survey-landing.service';
import { SurveyLandingData } from '../types/survey-landing.types';
import { AGE_RANGE_LABELS } from '../utils/age-range';
import { educationLabels, economicLabels, genderLabels } from '../utils/profile-labels';
import {
  DONT_KNOW_OPTION_LABEL,
  DONT_KNOW_OPTION_VALUE,
  NULL_OPTION_LABEL,
  NULL_OPTION_VALUE,
} from '../utils/questionOptions';
import type {
  ProfileField,
  QuestionAnalysisResult,
} from '../services/surveyAnalysis.service';

const { Title, Text } = Typography;

function formatDateTime(value: string): string {
  return new Date(value).toLocaleString('pt-BR');
}

const INTERNAL_VALUE_LABELS: Record<string, string> = {
  ...genderLabels,
  ...AGE_RANGE_LABELS,
  ...educationLabels,
  ...economicLabels,
  [NULL_OPTION_VALUE]: NULL_OPTION_LABEL,
  [DONT_KNOW_OPTION_VALUE]: DONT_KNOW_OPTION_LABEL,
};

const ANSWER_TYPE_LABELS: Record<string, string> = {
  MULTIPLE_CHOICE: 'Múltipla Escolha',
  CHECKBOX: 'Caixas de seleção',
  TEXT: 'texto livre',
  NUMBER: 'Número',
  DATE: 'Data',
  RATING: 'Avaliação',
  CHOICE: 'Múltipla Escolha',
  CHOICES: 'Caixas de seleção',
  BOOLEAN: 'Sim/Não',
};

const ANALYSIS_SEGMENT_OPTIONS: Array<{ value: ProfileField; label: string }> = [
  { value: 'gender', label: 'Gênero' },
  { value: 'age_group', label: 'Faixa Etária' },
  { value: 'education', label: 'Escolaridade' },
  { value: 'economic_level', label: 'Renda' },
  { value: 'state', label: 'Estado' },
];

function formatInternalValue(value: unknown): string {
  if (value === null || value === undefined) {
    return '-';
  }

  if (Array.isArray(value)) {
    return value.map((item) => formatInternalValue(item)).join(', ');
  }

  if (typeof value === 'boolean') {
    return value ? 'Sim' : 'Não';
  }

  if (typeof value === 'string') {
    return INTERNAL_VALUE_LABELS[value] ?? value;
  }

  if (typeof value === 'number' || typeof value === 'bigint') {
    return String(value);
  }

  return JSON.stringify(value);
}

function formatAnswerType(value: unknown): string {
  if (typeof value !== 'string') {
    return '-';
  }

  return ANSWER_TYPE_LABELS[value] ?? value;
}

export default function PublicAuditTrailPage() {
  const { surveyHash } = useParams<{ surveyHash: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [summary, setSummary] = useState<PublicAuditTrailSummary | null>(null);
  const [landingData, setLandingData] = useState<SurveyLandingData | null>(null);
  const [entries, setEntries] = useState<PublicAuditTrailEntriesResult | null>(null);
  const [quantitative, setQuantitative] = useState<QuantitativeSummaryResponse | null>(null);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [loadingEntries, setLoadingEntries] = useState(true);
  const [loadingQuantitative, setLoadingQuantitative] = useState(true);
  const [error, setError] = useState<string>('');

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailData, setDetailData] = useState<PublicAuditTrailEntryDetail | null>(null);
  const [detailError, setDetailError] = useState<string>('');
  const [analysisCriterion, setAnalysisCriterion] = useState<ProfileField>('gender');
  const [qualitativeView, setQualitativeView] = useState<'summary' | 'segmented'>('summary');
  const [hashSearch, setHashSearch] = useState('');
  const [downloadingComplementary, setDownloadingComplementary] = useState(false);
  const [activeTab, setActiveTab] = useState('participants');
  const screens = Grid.useBreakpoint();

  const loadEntries = async (page: number, search = hashSearch) => {
    if (!surveyHash) return;

    setLoadingEntries(true);
    try {
      const entriesResult = await auditTrailService.listEntries(surveyHash, page, 50, search || undefined);
      setEntries(entriesResult);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao carregar trilha de respostas');
    } finally {
      setLoadingEntries(false);
    }
  };

  const handleHashSearch = (value: string) => {
    setHashSearch(value);
    loadEntries(1, value || undefined);
  };

  useEffect(() => {
    if (!surveyHash) {
      setError('Hash da pesquisa não informada');
      setLoadingSummary(false);
      setLoadingEntries(false);
      setLoadingQuantitative(false);
      return;
    }

    const loadInitial = async () => {
      setLoadingSummary(true);
      setLoadingEntries(true);
      setLoadingQuantitative(true);
      setError('');

      try {
        const [summaryResult, entriesResult, quantitativeResult] = await Promise.all([
          auditTrailService.getSummary(surveyHash),
          auditTrailService.listEntries(surveyHash, 1, 50),
          auditTrailService.getQuantitativeSummary(surveyHash),
        ]);

        setSummary(summaryResult);
        setEntries(entriesResult);
        setQuantitative(quantitativeResult);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Erro ao carregar trilha pública');
      } finally {
        setLoadingSummary(false);
        setLoadingEntries(false);
        setLoadingQuantitative(false);
      }
    };

    loadInitial();
  }, [surveyHash]);

  // Quando a pesquisa ainda está ativa e o visitante não está logado, exibimos no
  // rodapé a mesma chamada para participação da landing page da pesquisa.
  useEffect(() => {
    if (authLoading || isAuthenticated || !surveyHash) {
      setLandingData(null);
      return;
    }

    if (summary?.header.status !== SurveyStatus.ACTIVE) {
      setLandingData(null);
      return;
    }

    let cancelled = false;
    surveyLandingService
      .getLandingData(surveyHash)
      .then((data) => {
        if (!cancelled) setLandingData(data);
      })
      .catch(() => {
        if (!cancelled) setLandingData(null);
      });

    return () => {
      cancelled = true;
    };
  }, [authLoading, isAuthenticated, surveyHash, summary?.header.status]);

  const canOpenDetail = summary?.header.status === SurveyStatus.COMPLETED;
  const canViewRestrictedResults = summary?.visibilityAccess?.isAllowed ?? false;
  const publishedQuestions =
    (summary?.publishedAnalysis?.payload?.questions as QuestionAnalysisResult[] | undefined) ?? [];

  const handleOpenDetail = async (responseHash: string) => {
    if (!surveyHash || !canOpenDetail) return;

    setDetailOpen(true);
    setDetailLoading(true);
    setDetailError('');
    setDetailData(null);

    try {
      const detail = await auditTrailService.getEntryDetail(surveyHash, responseHash);
      setDetailData(detail);
    } catch (err: any) {
      setDetailError(err.response?.data?.message || 'Erro ao carregar detalhe da resposta');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleComplementaryDownload = async () => {
    const endpoint = summary?.complementaryAnalysis?.attachment?.download?.endpoint;
    if (!endpoint) return;

    const match = endpoint.match(/\/surveys\/(\d+)\/analysis/);
    const surveyId = match ? Number(match[1]) : NaN;
    if (!Number.isFinite(surveyId)) return;

    try {
      setDownloadingComplementary(true);
      const fileName = summary?.complementaryAnalysis?.attachment?.fileName || 'analise-complementar.pdf';

      const presignedUrl = await auditTrailService.getComplementaryAttachmentDownloadUrl(surveyId);
      if (presignedUrl) {
        const anchor = document.createElement('a');
        anchor.href = presignedUrl;
        anchor.rel = 'noopener noreferrer';
        anchor.target = '_blank';
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        return;
      }

      const blob = await auditTrailService.downloadComplementaryAttachment(surveyId);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } finally {
      setDownloadingComplementary(false);
    }
  };

  const tabItems = [
    {
      key: 'participants',
      label: 'Perfil dos Participantes',
      children: (
        <PublicAuditTrailQuantitativeSummary
          data={quantitative}
          loading={loadingQuantitative}
        />
      ),
    },
    ...(canViewRestrictedResults
      ? [
          {
            key: 'qualitative',
            label: 'Indicadores Qualitativos',
            children: summary?.publishedAnalysis?.payload ? (
        <>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
            <Segmented
              value={qualitativeView}
              onChange={(v) => setQualitativeView(v as 'summary' | 'segmented')}
              options={[
                { label: 'Resumo Geral', value: 'summary' },
                { label: 'Por Perfil', value: 'segmented' },
              ]}
            />
          </div>

          {qualitativeView === 'summary' && (
            <AnalysisSummaryView questions={publishedQuestions} />
          )}

          {qualitativeView === 'segmented' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <Text strong>Segmentar por:</Text>
                <Select
                  value={analysisCriterion}
                  onChange={(value) => setAnalysisCriterion(value)}
                  options={ANALYSIS_SEGMENT_OPTIONS}
                  style={{ width: 180 }}
                />
              </div>
              <AnalyticsSegmentedView
                questions={publishedQuestions}
                criterion={analysisCriterion}
              />
            </div>
          )}
          <Text type="secondary">
            Publicada em{' '}
            {summary.publishedAnalysis.publishedAt
              ? formatDateTime(summary.publishedAnalysis.publishedAt)
              : '-'}
            {' · '}
            {summary.publishedAnalysis.payload.total_respondents} respondentes
          </Text>
        </>
      ) : (
        <Notice
          type="info"
          showIcon
          message="Análise qualitativa ainda não publicada"
        />
      ),
          },
        ]
      : []),
    {
      key: 'answers',
      label: 'Trilha de Respostas',
      children: (
        <PublicAuditTrailEntries
          entries={entries}
          loading={loadingEntries}
          canOpenDetail={Boolean(canOpenDetail)}
          onOpenDetail={handleOpenDetail}
          onPageChange={loadEntries}
          onSearch={handleHashSearch}
        />
      ),
    },
    ...(canViewRestrictedResults
      ? [
          {
            key: 'complementary',
            label: 'Análise Complementar',
            children: summary?.complementaryAnalysis ? (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <div
            style={{ border: '1px solid #f0f0f0', borderRadius: 8, padding: 16 }}
            dangerouslySetInnerHTML={{
              __html: summary.complementaryAnalysis.contentHtml,
            }}
          />

          {summary.complementaryAnalysis.attachment && (
            <Space>
              <Text strong>{summary.complementaryAnalysis.attachment.fileName}</Text>
              <Button
                type="primary"
                loading={downloadingComplementary}
                disabled={!summary.complementaryAnalysis.attachment.download.allowed}
                onClick={handleComplementaryDownload}
              >
                Baixar PDF
              </Button>
            </Space>
          )}

          {summary.complementaryAnalysis.attachment &&
            !summary.complementaryAnalysis.attachment.download.allowed && (
              <Notice
                type="warning"
                showIcon
                message="Download indisponível"
                description="Faça login com uma conta autorizada para baixar o anexo complementar."
              />
            )}
          <Text type="secondary">
            Publicada em{' '}
            {summary.complementaryAnalysis.publishedAt
              ? formatDateTime(summary.complementaryAnalysis.publishedAt)
              : '-'}
          </Text>
        </Space>
      ) : (
        <Notice
          type="info"
          showIcon
          message="Análise complementar não publicada para esta pesquisa ou pesquisa ainda não concluída"
        />
      ),
          },
        ]
      : []),
  ];

  useEffect(() => {
    const availableKeys = new Set(tabItems.map((item) => item.key));
    if (!availableKeys.has(activeTab)) {
      setActiveTab('participants');
    }
  }, [activeTab, tabItems]);

  if (loadingSummary) {
    return (
      <PublicHomeLayout>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 320 }}>
          <Spin size="large" />
        </div>
      </PublicHomeLayout>
    );
  }

  return (
    <PublicHomeLayout>
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '32px 24px 64px' }}>
        <Button
          icon={<ArrowLeftOutlined />}
          type="link"
          onClick={() => navigate('/')}
          style={{ padding: 0, marginBottom: 24, color: '#2E2B81' }}
        >
          Voltar para Pesquisas
        </Button>

        <div style={{ marginBottom: 24 }}>
          <Text style={{ fontSize: 13, color: '#888', textTransform: 'uppercase', letterSpacing: 1 }}>
            Transparência Pública
          </Text>
          <Title level={2} style={{ margin: '4px 0 0' }}>
            {summary ? summary.header.name : 'Trilha de Auditoria'}
          </Title>
          {summary && (
            <Text type="secondary">Registro público de participações desta pesquisa</Text>
          )}
        </div>

      {error && (
        <Alert
          type="error"
          showIcon
          message="Falha ao carregar trilha pública"
          description={error}
          style={{ marginBottom: 16 }}
        />
      )}

      {summary && <PublicAuditTrailHeader summary={summary} />}

      {summary && !canViewRestrictedResults && (
        <Notice
          type="info"
          showIcon
          message="Indicadores qualitativos e análise complementar indisponíveis"
          description={`A visualização desses dados segue a regra de visibilidade da pesquisa: ${summary.header.visibility?.disclosureRuleText ?? 'resultado ainda não liberado'}.`}
          style={{ marginBottom: 16 }}
        />
      )}

      <Card style={{ marginBottom: 16 }}>
        {!screens.md && (
          <Select
            value={activeTab}
            onChange={setActiveTab}
            style={{ width: '100%', marginBottom: 16 }}
            options={[
              { value: 'participants', label: 'Perfil dos Participantes' },
              ...(canViewRestrictedResults ? [{ value: 'qualitative', label: 'Indicadores Qualitativos' }] : []),
              { value: 'answers', label: 'Trilha de Respostas' },
              ...(canViewRestrictedResults ? [{ value: 'complementary', label: 'Análise Complementar' }] : []),
            ]}
          />
        )}

        {screens.md ? (
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={tabItems}
          />
        ) : (
          tabItems.find((item) => item.key === activeTab)?.children
        )}
      </Card>

      {!isAuthenticated &&
        summary?.header.status === SurveyStatus.ACTIVE &&
        landingData && (
          <Card style={{ marginBottom: 16 }}>
            <Title level={4} style={{ marginTop: 0 }}>
              Participe desta pesquisa
            </Title>
            <Text type="secondary">
              Esta pesquisa ainda está ativa. Entre ou crie sua conta para registrar sua resposta.
            </Text>
            <SurveyLandingCTA
              surveyId={landingData.surveyId}
              surveyHash={landingData.surveyHash}
              hasUserParticipated={landingData.hasUserParticipated}
              status={landingData.status}
              allowGuestParticipation={landingData.allowGuestParticipation}
              showAuditTrailLink={false}
            />
          </Card>
        )}

      <Modal
        open={detailOpen}
        onCancel={() => setDetailOpen(false)}
        footer={null}
        title="Detalhe da Resposta"
        width={800}
      >
        {detailLoading && <Spin />}
        {detailError && <Alert type="error" showIcon message={detailError} />}

        {!detailLoading && !detailError && detailData && (
          <>
            <Descriptions column={1} size="small" style={{ marginBottom: 12 }}>
              <Descriptions.Item label="Hash">{detailData.responseHash}</Descriptions.Item>
              <Descriptions.Item label="Respondido em">
                {formatDateTime(detailData.respondedAt)}
              </Descriptions.Item>
            </Descriptions>

            <Table
              rowKey={(row) => String(row.questionId)}
              dataSource={detailData.answersQualified}
              pagination={false}
              scroll={{ y: 420 }}
              columns={[
                { title: 'Pergunta', dataIndex: 'questionLabel', key: 'questionLabel' },
                {
                  title: 'Tipo',
                  dataIndex: 'answerType',
                  key: 'answerType',
                  width: 160,
                  render: (value: unknown) => formatAnswerType(value),
                },
                {
                  title: 'Resposta',
                  dataIndex: 'answerValue',
                  key: 'answerValue',
                  render: (value: unknown) => formatInternalValue(value),
                },
              ]}
            />
          </>
        )}
      </Modal>
      </div>
    </PublicHomeLayout>
  );
}
